/*
 * voucherPdf — un comprobante de una página para gastos / cobros que reutiliza
 * el marco de marca de Meos (`drawBrandedFrame`) del `invoicePdf.js`. No
 * duplica lógica ni fuentes; el marco cuida logo, watermark y ola/banda al pie.
 *
 * Uso:
 *   downloadVoucher(record, "expense" | "payment", { money, emisor, lang })
 *
 * NOTE: Translations come from pdfStr(lang) — never from the UI i18n — because
 * the user may choose a per-PDF language distinct from the UI locale.
 * The Amiri Arabic font MUST be registered via ensureArabicFont(doc) before
 * any pdfText() call that may contain Arabic characters, otherwise Arabic
 * glyphs render as empty squares.
 */
import jsPDF from "jspdf";
import { BRAND, drawBrandedFrame } from "@/lib/invoicePdf";
import { pdfStr, isRTL } from "@/lib/pdfStrings";
import { pdfText, ensureArabicFont } from "@/lib/pdfFonts";

export async function downloadVoucher(record, kind, { money, emisor = {}, lang = "es" } = {}) {
  const doc = new jsPDF({ unit: "pt", format: "a4", compress: true });
  // Register Amiri so Arabic text in headers/labels/values renders correctly.
  await ensureArabicFont(doc);

  const S = pdfStr(lang);
  const rtl = isRTL(lang);
  const W = doc.internal.pageSize.getWidth();

  // -------- Header + watermark + curved footer ----------------------------
  const titleText = kind === "expense" ? S.expense_voucher : S.payment_voucher;
  const cursorY = await drawBrandedFrame(doc, {
    subtitle: (record.oficina || S.brand_slogan) + "",
    lang, emisor, showWatermark: true,
  });

  // Draw the big red title just under the header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.setTextColor(...BRAND.red);
  pdfText(doc, titleText, W / 2, cursorY + 4, { align: "center", lang });

  // -------- Meta block ----------------------------------------------------
  let y = cursorY + 30;
  const box = { x: 40, w: W - 80, h: 180 };
  doc.setFillColor(...BRAND.navySoft);
  doc.roundedRect(box.x, y, box.w, box.h, 8, 8, "F");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.navyDark);
  const moneyFmt = (v, c) => (money ? money(v, c) : `${v ?? 0} ${c || ""}`);
  const rows = kind === "expense" ? [
    [S.date,        record.fecha || "—"],
    [S.category,    record.categoria || "—"],
    [S.description, record.descripcion || "—"],
    [S.supplier,    record.proveedor || "—"],
    [S.office,      record.oficina || "—"],
    [S.method,      record.metodo_pago || "—"],
    [S.amount,      moneyFmt(record.monto, record.moneda)],
    [S.total,       moneyFmt(record.total ?? record.monto, record.moneda)],
  ] : [
    [S.date,      record.fecha || "—"],
    [S.reference, record.referencia || record.invoice_numero || "—"],
    [S.client,    record.cliente_nombre || "—"],
    [S.office,    record.oficina || "—"],
    [S.method,    record.metodo || record.metodo_pago || "—"],
    [S.amount,    moneyFmt(record.monto, record.moneda)],
  ];
  let ly = y + 24;
  // RTL: labels anchored to the right side of the inner box, values towards the center.
  const labelX = rtl ? (box.x + box.w - 18) : (box.x + 18);
  const valueX = rtl ? (box.x + box.w - 140) : (box.x + 140);
  const align = rtl ? "right" : "left";
  for (const [label, value] of rows) {
    doc.setFont("helvetica", "bold");
    pdfText(doc, `${label}:`, labelX, ly, { lang, align });
    doc.setFont("helvetica", "normal");
    pdfText(doc, String(value ?? "—"), valueX, ly, { lang, align });
    ly += 18;
  }

  // -------- Notes ---------------------------------------------------------
  const notes = record.notas || record.referencia || "";
  if (notes) {
    y = ly + 16;
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(40, y, W - 80, 62, 6, 6, "S");
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...BRAND.navyDark);
    const noteLabelX = rtl ? (W - 52) : 52;
    pdfText(doc, `${S.observations}:`, noteLabelX, y + 20, { lang, align });
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...BRAND.ink);
    const wrapped = doc.splitTextToSize(notes, W - 120);
    let ny = y + 38;
    for (const line of wrapped.slice(0, 2)) {
      pdfText(doc, line, noteLabelX, ny, { lang, align });
      ny += 14;
    }
  }

  const fname = kind === "expense"
    ? `gasto_${record.id?.slice(0, 8) || "voucher"}.pdf`
    : `cobro_${record.id?.slice(0, 8) || "voucher"}.pdf`;
  doc.save(fname);
}
